import * as Dropdown from "@radix-ui/react-dropdown-menu";
import * as Sentry from "@sentry/react";
import { tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";
import { Building2, Check, ChevronRight } from "lucide-react";
import { enqueueSnackbar } from "notistack";

/**
 * PRTL-1348: multi-account switcher, rendered as a "Switch Organization"
 * entry inside the profile (avatar) dropdown in the header. It must be
 * mounted inside that menu's Dropdown.Root — it renders a Radix submenu
 * that flies out the org list. Shown only when the user belongs to two or
 * more organizations (multi-org is always on; the flag was removed).
 *
 * Switching mints a token scoped to the chosen org (the previous session
 * is revoked server-side) and hard-resets the app so no cached data from
 * the previous org survives.
 */
export const OrgSwitcherMenuItem = () => {
    const { data: user } = trpc.users.me.useQuery();
    const utils = trpc.useUtils();
    const switchMutation = trpc.auth.switchOrganization.useMutation({
        onSuccess: ({ token }) => {
            tokenManager.set(token);
            window.location.assign("/");
        },
        onError: (e) => {
            // Membership may have been revoked since users.me loaded — the
            // server re-checks. Surface it and refresh the membership list.
            Sentry.captureException(e, { extra: { action: "switchOrganization" } });
            enqueueSnackbar(e.message || "Could not switch organizations. Please try again.", {
                variant: "error",
            });
            void utils.users.me.invalidate();
        },
    });

    const memberships = user?.memberships ?? [];
    if (memberships.length < 2) return null;

    return (
        <li className="rounded-full bg-[#eeeeee] p-2 px-4">
            <Dropdown.Sub>
                <Dropdown.SubTrigger
                    disabled={switchMutation.isPending}
                    className="block w-full cursor-pointer font-sans text-[17px] leading-normal font-normal text-[#424242] outline-hidden focus:ring-0 focus:outline-hidden"
                >
                    <div className="space-x-2 flex flex-row items-center">
                        <div>
                            <Building2 className="h-[24px] w-[24px]" />
                        </div>
                        <div>Switch Organization</div>
                        <ChevronRight className="ml-auto! h-4 w-4 text-gray-500" />
                    </div>
                </Dropdown.SubTrigger>
                <Dropdown.Portal>
                    <Dropdown.SubContent
                        sideOffset={16}
                        className="z-50 w-[260px] rounded-2xl border border-[#d4d5d5] bg-white p-2 shadow-lg"
                    >
                        <Dropdown.Label className="px-2 pt-1.5 pb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Organizations
                        </Dropdown.Label>
                        {memberships.map((m) => {
                            const isActive = m.organizationId === user?.activeOrganizationId;
                            return (
                                <Dropdown.Item
                                    key={m.organizationId}
                                    disabled={switchMutation.isPending}
                                    onSelect={(e) => {
                                        if (isActive) return;
                                        e.preventDefault();
                                        switchMutation.mutate({
                                            organizationId: m.organizationId,
                                        });
                                    }}
                                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-[#424242] outline-hidden radix-highlighted:bg-gray-100"
                                >
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-atlantic text-xs font-semibold text-white">
                                        {m.organizationName.charAt(0).toUpperCase()}
                                    </span>
                                    <span className="flex-1 truncate font-medium">
                                        {m.organizationName}
                                    </span>
                                    {isActive && <Check className="h-4 w-4 text-[#0F8EA6]" />}
                                    {switchMutation.isPending &&
                                        switchMutation.variables?.organizationId ===
                                            m.organizationId && (
                                            <span className="text-xs text-gray-500">
                                                Switching…
                                            </span>
                                        )}
                                </Dropdown.Item>
                            );
                        })}
                    </Dropdown.SubContent>
                </Dropdown.Portal>
            </Dropdown.Sub>
        </li>
    );
};
